/**
 * ActivityToasts — Floating toast notifications for real-time entity changes.
 * Shows when another user creates, updates, or deletes entities.
 */

import { usePresenceStore } from "@/stores/presence";
import { X, Info, AlertTriangle, CheckCircle } from "lucide-react";
import { cn } from "@/lib/utils";

const TOAST_ICONS = {
  info: Info,
  warning: AlertTriangle,
  success: CheckCircle,
} as const;

const TOAST_COLORS = {
  info: "border-blue-200 bg-blue-50 text-blue-800 dark:border-blue-800 dark:bg-blue-950 dark:text-blue-200",
  warning: "border-amber-200 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200",
  success: "border-green-200 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-950 dark:text-green-200",
} as const;

export function ActivityToasts() {
  const toasts = usePresenceStore((s) => s.toasts);
  const dismissToast = usePresenceStore((s) => s.dismissToast);

  if (toasts.length === 0) return null;

  return (
    <div className="fixed bottom-4 right-4 z-50 flex flex-col gap-2 max-w-sm">
      {toasts.map((toast) => {
        const Icon = TOAST_ICONS[toast.type];
        return (
          <div
            key={toast.id}
            className={cn(
              "flex items-center gap-2.5 rounded-lg border px-3.5 py-2.5 shadow-lg animate-slide-in",
              TOAST_COLORS[toast.type],
            )}
          >
            <Icon className="h-4 w-4 shrink-0" />
            <span className="flex-1 text-sm font-medium">{toast.message}</span>
            <button
              onClick={() => dismissToast(toast.id)}
              className="shrink-0 rounded p-0.5 opacity-60 hover:opacity-100 transition-opacity"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
